import {
  PlainDate,
  type PlainDateString,
  toPlainDateString,
} from "@sa-nu/shared-utils/date";
import { formatCurrency } from "@sa-nu/shared-utils/price";

import type {
  DatePickerDateInfo,
  DatePickerSelection,
} from "../../../dx/ui/date-picker";
import { UNAVAILABLE_PRICE_LABEL } from "../../../reservation-form/_shared/price-labels";
import type { NightAvailability } from "../../_utils/room-type-stay-availability";
import { selectableCheckoutDates } from "../../_utils/room-type-stay-availability";

/** NightAvailability に price を加えた最小シェイプ（カレンダー用）。 */
type PricedNight = NightAvailability & { price: number };

type BuildDateInfoMapArgs = {
  nights: readonly PricedNight[];
  selection: DatePickerSelection;
  minDate: PlainDate;
  maxDate: PlainDate;
};

export function buildDateInfoMap({
  nights,
  selection,
  minDate,
  maxDate,
}: BuildDateInfoMapArgs): Record<PlainDateString, DatePickerDateInfo> {
  const { startDate, endDate } = selection;

  // night 文字列 → price の高速ルックアップ
  const priceByNight = new Map(
    nights.map((n) => [n.night.toString(), n.price]),
  );

  // Phase: choosing CI (startDate not yet set)
  if (startDate === null) {
    return buildCheckinPhaseMap(nights, minDate, maxDate, priceByNight);
  }

  // Phase: choosing CO (startDate set, endDate not yet set)
  if (endDate === null) {
    return buildCheckoutPhaseMap(
      nights,
      startDate,
      minDate,
      maxDate,
      priceByNight,
    );
  }

  // Phase: confirmed
  return buildConfirmedPhaseMap(
    nights,
    endDate,
    minDate,
    maxDate,
    priceByNight,
  );
}

/**
 * CI 選択フェーズの dateInfoMap を構築する。
 * [minDate, maxDate] の全日付を明示的にカバーし、nights に含まれていない
 * ギャップ日や予約不可日を { disabled: true } でマークする。
 * 空き日には price を表示する。
 */
function buildCheckinPhaseMap(
  nights: readonly PricedNight[],
  minDate: PlainDate,
  maxDate: PlainDate,
  priceByNight: Map<string, number>,
): Record<PlainDateString, DatePickerDateInfo> {
  // night 文字列 → availableAccommodationIds の高速ルックアップ
  const nightMap = new Map(
    nights.map((n) => [n.night.toString(), n.availableAccommodationIds]),
  );

  const result: Record<PlainDateString, DatePickerDateInfo> = {};

  let current = minDate;
  while (PlainDate.compare(current, maxDate) <= 0) {
    const key = toPlainDateString(current);
    const availableIds = nightMap.get(current.toString());
    const hasAvailability =
      availableIds !== undefined &&
      availableIds.length > 0 &&
      selectableCheckoutDates({ nights, firstNight: current }).length > 0;
    const rawPrice = priceByNight.get(current.toString());
    const price =
      hasAvailability && rawPrice !== undefined
        ? formatCurrency(rawPrice)
        : undefined;
    result[key] = { disabled: !hasAvailability, price };
    current = current.add({ days: 1 });
  }

  return result;
}

/**
 * CO 選択フェーズの dateInfoMap を構築する。
 * [minDate, maxDate] の全日付を明示的にカバーし、有効な CO 日のみ有効化する。
 * CO 日は lastNight+1 なので maxDate+1 になることがあるが、それは呼び出し側が
 * CO 選択中に selectableMaxDate を maxDate+1 に拡張することで選択可能にしている。
 * 空き泊日には price を表示する（CO 日自体は stay night でないため price なし）。
 */
function buildCheckoutPhaseMap(
  nights: readonly PricedNight[],
  startDate: PlainDate,
  minDate: PlainDate,
  maxDate: PlainDate,
  priceByNight: Map<string, number>,
): Record<PlainDateString, DatePickerDateInfo> {
  const checkoutDates = selectableCheckoutDates({
    nights,
    firstNight: startDate,
  });
  const checkoutSet = new Set(checkoutDates.map((d) => d.toString()));

  // night 文字列 → availableAccommodationIds（在庫の有無判定用）
  const nightMap = new Map(
    nights.map((n) => [n.night.toString(), n.availableAccommodationIds]),
  );

  const result: Record<PlainDateString, DatePickerDateInfo> = {};

  // Walk every date in [minDate, maxDate] and mark each one explicitly.
  let current = minDate;
  while (PlainDate.compare(current, maxDate) <= 0) {
    const key = toPlainDateString(current);
    // 在庫のある夜だけ price を出す（CI フェーズと同条件）。在庫ゼロ夜は
    // nights に price を持つが CI フェーズでは隠れるため、CO フェーズでも隠す。
    const availableIds = nightMap.get(current.toString());
    const hasInventory = availableIds !== undefined && availableIds.length > 0;
    const rawPrice = priceByNight.get(current.toString());
    const price =
      hasInventory && rawPrice !== undefined
        ? formatCurrency(rawPrice)
        : undefined;
    if (current.equals(startDate)) {
      // The selected CI date remains enabled (non-disabled) in CO phase
      result[key] = { disabled: false, price };
    } else {
      result[key] = { disabled: !checkoutSet.has(current.toString()), price };
    }
    current = current.add({ days: 1 });
  }

  // CO dates are lastNight+1 and may be outside [minDate, maxDate].
  // Add them explicitly so the extended picker range shows them as enabled.
  for (const co of checkoutDates) {
    const key = toPlainDateString(co);
    if (!(key in result)) {
      result[key] = { disabled: false };
    }
  }

  // DatePicker は map に無い日を選択可能として扱うため、CO 選択中だけ選択範囲に入る maxDate+1 が
  // CO 候補でなければ明示的に無効化する。
  result[toPlainDateString(maxDate.add({ days: 1 }))] ??= { disabled: true };

  return result;
}

/**
 * 確定フェーズの dateInfoMap を構築する。
 * CI 制約を再適用して gap/満室日が誤って enabled にならないようにしつつ、
 * CO 日 (= endDate) の料金セルは宿泊料金が発生しないため UNAVAILABLE_PRICE_LABEL で上書きする。
 */
function buildConfirmedPhaseMap(
  nights: readonly PricedNight[],
  endDate: PlainDate,
  minDate: PlainDate,
  maxDate: PlainDate,
  priceByNight: Map<string, number>,
): Record<PlainDateString, DatePickerDateInfo> {
  const map = buildCheckinPhaseMap(nights, minDate, maxDate, priceByNight);
  const endKey = toPlainDateString(endDate);
  map[endKey] = {
    ...(map[endKey] ?? { disabled: false }),
    price: UNAVAILABLE_PRICE_LABEL,
  };
  return map;
}
